import React, { useState, useEffect } from "react";
import { Container, Spinner, Row, Col } from "react-bootstrap";
import showToast from "helpers/showToast";
import { motion } from "framer-motion";
import { getSiteSettings, updateSiteSettings } from "../../../services/site-settings-service";
import { mergeExtraSettings } from "constants/SiteSettingsConstants";

// Import components
import SiteSettingsHeader from "./components/SiteSettingsHeader";
import QuickStatsCards from "./components/QuickStatsCards";
import GeneralSettings from "./components/GeneralSettings";
import ImageSettings from "./components/ImageSettings";
import ExtraSettings from "./components/ExtraSettings";
import SMSTimeSettings from "./components/SMSTimeSettings";
import SuperAdmins from "./components/SuperAdmins";

function SiteSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getSiteSettings();
      const settings = response.data;
      const mergedSettings = {
        ...settings,
        extraSettings: mergeExtraSettings(settings.extraSettings)
      };
      setSettings(mergedSettings);
    } catch (error) {
      showToast("Ayarlar yüklenirken bir hata oluştu", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (settings) {
      setSettings({
        ...settings,
        [name]: type === "checkbox" ? checked : value
      });
    }
  };

  const handleExtraSettingsToggle = (index, checked) => {
    if (settings && settings.extraSettings) {
      const updatedExtraSettings = [...settings.extraSettings];
      updatedExtraSettings[index] = {
        ...updatedExtraSettings[index],
        enabled: checked
      };

      setSettings({
        ...settings,
        extraSettings: updatedExtraSettings
      });
    }
  };

  const handleFieldChange = (settingIndex, fieldIndex, value) => {
    if (settings && settings.extraSettings) {
      const updatedExtraSettings = [...settings.extraSettings];
      if (updatedExtraSettings[settingIndex]?.fields?.[fieldIndex]) {
        updatedExtraSettings[settingIndex].fields[fieldIndex].value = value;
        
        setSettings({
          ...settings,
          extraSettings: updatedExtraSettings
        });
      }
    }
  };

  const handleSaveSettings = async (filledSettings = null) => {
    try {
      setSaving(true);
      if (!settings) return;
      const currentSettings = filledSettings !== null ? filledSettings : settings;
      
      const settingsToSave = {
        ...currentSettings,
        extraSettings: mergeExtraSettings(currentSettings.extraSettings)
      };
      
      await updateSiteSettings(settingsToSave);
      showToast("Ayarlar başarıyla kaydedildi!", { type: "success" });
    } catch (error) {
      showToast("Ayarlar kaydedilirken bir hata oluştu", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getSha1FieldName = (type) => {
    switch (type) {
      case "logo":
        return "logoSHA1";
      case "favicon":
        return "faviconSHA1";
      case "register_form_banner":
        return "registerFormBannerSHA1";
      case "login_left_banner":
        return "loginLeftBannerSHA1";
      case "sinav_yonerge":
        return "sinavYonergeUrlSHA1";
      default:
        return "";
    }
  };

  const updateImage = async (type, value) => {
    try {
      const updatedSettings = {
        ...settings,
        [getSha1FieldName(type)]: value
      };
      setSettings(updatedSettings);
      handleSaveSettings(updatedSettings);
    } catch (error) {
      showToast("Görsel güncellenirken bir hata oluştu", { type: "error" });
    }
  };

  const deleteImage = async (type) => {
    try {
      const updatedSettings = {
        ...settings,
        [getSha1FieldName(type)]: null
      };
      setSettings(updatedSettings);
      handleSaveSettings(updatedSettings);
    } catch (error) {
      showToast("Görsel silinirken bir hata oluştu", { type: "error" });
    }
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Ayarlar yükleniyor...</p>
        </div>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="pb-5"
    >
      <Container>
        <SiteSettingsHeader handleSaveSettings={handleSaveSettings} saving={saving} />

        <QuickStatsCards />

        <Row>
          <Col md={12}>
            <GeneralSettings
              settings={settings}
              handleInputChange={handleInputChange}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
            />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <SMSTimeSettings settings={settings} handleInputChange={handleInputChange} />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <ExtraSettings
              settings={settings}
              handleExtraSettingsToggle={handleExtraSettingsToggle}
              handleFieldChange={handleFieldChange}
            />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <SuperAdmins />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={12}>
            <ImageSettings
              settings={settings}
              updateImage={updateImage}
              deleteImage={deleteImage}
              handleSaveSettings={handleSaveSettings}
            />
          </Col>
        </Row>
      </Container>
    </motion.div>
  );
}

export default SiteSettings;
